import React, { useState } from "react";
// MUI components
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Radio,
  FormControlLabel,
  TextField,
  Grid,
  Tab,
  Tabs,
  Button,
  Autocomplete,
  FormHelperText,
} from "@mui/material";
// MUI Icons
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
// react admin
import {
  useGetList,
  useDataProvider,
  useNotify,
  useRedirect,
  TopToolbar,
} from "react-admin";
// loader
import { Loader } from "../../Loader";
// Firelink component
import FirelinkGameRTPForm from "./FirelinkGameRTPForm";
// Keno component
import KenoGameRTPForm from "./KenoGameRTPForm";

// Categorized symbols
const lowerSymbols = ["L1", "L2", "L3", "L4", "L5", "L6"];
const midSymbols = ["M1", "M2", "M3"];
const higherSymbols = ["H1", "H2", "H3"];
const specialSymbols = ["Scatter", "Wild", "2StackWild", "3StackWild"];

// RTP Configurations
const rtpConfigs = [85, 88, 90, 92, 95, 98]; // RTP values

// Function to check if a game is a Firelink game
const isFirelinkGame = (gameName) => {
  if (!gameName) return false;
  const firelinkGameNames = [
    'Fire Ball Havana',
    'Fire Ball Las Vegas',
    'Fire Ball Chinatown',
    "Anubis' Secret Tomb",
    'Jewels of Olympus',
    'Wolf Run',
    'Buffalo Gold Rush',
  ];
  return firelinkGameNames.includes(gameName);
};

// Function to check if a game is a Keno game
const isKenoGame = (gameName) => {
  if (!gameName) return false;
  const kenoGameNames = [
    'Hexa Keno',
    'Jungle Keno',
  ];
  return kenoGameNames.includes(gameName);
};

const NewCreateGameRTP = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();

  const [loading, setLoading] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [selectedRTP, setSelectedRTP] = useState("85"); // Default selected RTP
  const [expanded, setExpanded] = useState(); // Default expanded panel
  const [selectedTab, setSelectedTab] = useState(0); // Default selected reel tab (Reel 1)
  const [gameError, setGameError] = useState(""); // Track game selection error

  const { data, isPending, isLoading } = useGetList(
    "gameList",
    {
      pagination: { page: 1, perPage: 10000 },
      sort: { field: "createdAt", order: "DESC" },
    },
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    }
  );

  // Function to initialize symbol values for each reel
  const symbolValuesForReel = () => {
    return {
      ...lowerSymbols.reduce((acc, symbol) => {
        acc[symbol] = "";
        return acc;
      }, {}),
      ...midSymbols.reduce((acc, symbol) => {
        acc[symbol] = "";
        return acc;
      }, {}),
      ...higherSymbols.reduce((acc, symbol) => {
        acc[symbol] = "";
        return acc;
      }, {}),
      ...specialSymbols.reduce((acc, symbol) => {
        acc[symbol] = "";
        return acc;
      }, {}),
    };
  };

  // State to store symbol values for each RTP and each reel
  const [symbolValues, setSymbolValues] = useState(
    rtpConfigs.reduce((acc, rtp) => {
      acc[rtp] = {
        reel1: symbolValuesForReel(),
        reel2: symbolValuesForReel(),
        reel3: symbolValuesForReel(),
        reel4: symbolValuesForReel(),
        reel5: symbolValuesForReel(),
      };
      return acc;
    }, {})
  );

  // State to track whether each RTP is active or not
  const [activeRTP, setActiveRTP] = useState(
    rtpConfigs.reduce((acc, rtp) => {
      acc[rtp] = rtp === 85 ? true : false; // Default active RTP is 85
      return acc;
    }, {})
  );

  // Handle RTP selection
  const handleRTPChange = (event) => {
    setSelectedRTP(event.target.value);
    setActiveRTP((prev) => ({
      ...prev,
      [event.target.value]: true,
    }));
    // Deactivate other RTPs
    rtpConfigs
      .filter((rtp) => rtp !== parseInt(event.target.value))
      .forEach((rtp) => {
        setActiveRTP((prev) => ({
          ...prev,
          [rtp]: false,
        }));
      });
  };

  // Handle accordion expand
  const handleExpand = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  // Handle tab change for reel selection
  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  // Handle symbol value change for the selected reel
  const handleSymbolChange = (rtp, reel, symbol, newValue) => {
    setSymbolValues((prev) => ({
      ...prev,
      [rtp]: {
        ...prev[rtp],
        [reel]: { ...prev[rtp][reel], [symbol]: newValue },
      },
    }));
  };

  // Submit function to log the data
  const handleSubmit = async () => {
    // Check if game is selected
    if (!selectedGame) {
      setGameError("Please select a game.");
      return;
    }
    setGameError(""); // Reset error if game is selected

    setLoading(true);

    // Find the active RTP
    const activeRTPKey = Object.keys(activeRTP).find(
      (rtp) => activeRTP[rtp] === true
    );

    // Convert symbol values to numbers during submission
    const formatSymbolValues = (symbols) => {
      return Object.fromEntries(
        Object.entries(symbols).map(([key, value]) => [
          key,
          value === "" ? 0 : Number(value),
        ])
      );
    };

    const output = rtpConfigs.map((rtp) => ({
      rtp: rtp,
      isActive: activeRTP[rtp],
      data: {
        reel1: formatSymbolValues(symbolValues[rtp].reel1),
        reel2: formatSymbolValues(symbolValues[rtp].reel2),
        reel3: formatSymbolValues(symbolValues[rtp].reel3),
        reel4: formatSymbolValues(symbolValues[rtp].reel4),
        reel5: formatSymbolValues(symbolValues[rtp].reel5),
      },
    }));

    const formatedData = {
      gameId: selectedGame?.id,
      gameName: selectedGame?.name,
      gameRtp: Number(activeRTPKey),
      reelName: output,
    };

    try {
      const response = await dataProvider.create("gameRtp", {
        data: formatedData,
      });

      if (response) {
        notify("GameRTP created successfully!", {
          type: "success",
        });
        redirect("/gameRtp");
        setLoading(false);
      }
    } catch (error) {
      notify(error.message, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  if (isPending) {
    return <Loader />;
  }

  return (
    <React.Fragment>
      {loading && <Loader />}
      <TopToolbar>
        <Button
          label="Create"
          variant="contained"
          color="primary"
          name="newrecord"
          startIcon={<ArrowBackIcon />}
          sx={{
            mt: 3,
            background: "#FFC107",
            color: "#0A0A0A",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#FFA000",
            },
          }}
          onClick={() => redirect("/GameRtp")}
        >
          Back
        </Button>
      </TopToolbar>
      <Grid container spacing={2} sx={{ mt: 2 }}>
        {/* Game selection */}
        <Grid item xs={12}>
          <Autocomplete
            value={selectedGame}
            onChange={(event, newValue) => setSelectedGame(newValue)}
            options={data || []}
            getOptionLabel={(option) => option.name} // Assuming the game list has a 'name' field
            renderInput={(params) => (
              <>
                <TextField {...params} label="Select a Game" />
                {gameError && (
                  <FormHelperText error>{gameError}</FormHelperText>
                )}
              </>
            )}
            isLoading={isLoading}
            disableClearable
          />
        </Grid>

        <Grid item xs={12}>
          {selectedGame && isKenoGame(selectedGame?.name) ? (
            <KenoGameRTPForm selectedGame={selectedGame} />
          ) : selectedGame && isFirelinkGame(selectedGame?.name) ? (
            <FirelinkGameRTPForm selectedGame={selectedGame} />
          ) : (
            rtpConfigs.map((rtp, index) => (
            <Accordion
              key={rtp}
              expanded={expanded === `panel${index}`}
              onChange={handleExpand(`panel${index}`)}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel${index}-content`}
                id={`panel${index}-header`}
              >
                <FormControlLabel
                  value={rtp.toString()}
                  control={
                    <Radio
                      checked={selectedRTP === rtp.toString()}
                      onChange={handleRTPChange}
                    />
                  }
                  label={<Typography>{rtp}% RTP</Typography>}
                  onClick={(e) => e.stopPropagation()} // Prevents accordion toggle when clicking radio
                  onFocus={(e) => e.stopPropagation()}
                />
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="subtitle1">
                  Symbol Distribution:
                </Typography>

                {/* Tabs for selecting Reel 1 to Reel 5 */}
                <Tabs
                  value={selectedTab}
                  onChange={handleTabChange}
                  aria-label="reel tabs"
                  centered
                >
                  <Tab label="Reel 1" />
                  <Tab label="Reel 2" />
                  <Tab label="Reel 3" />
                  <Tab label="Reel 4" />
                  <Tab label="Reel 5" />
                </Tabs>

                <Typography variant="h6" mt={2}>
                  {`Reel ${selectedTab + 1} Symbol Distribution:`}
                </Typography>
                <Grid container spacing={2} mt={1}>
                  {lowerSymbols.map((symbol) => (
                    <Grid item xs={6} sm={4} key={symbol}>
                      <TextField
                        label={`Symbol ${symbol}`}
                        fullWidth
                        value={
                          symbolValues[rtp][`reel${selectedTab + 1}`][symbol]
                        }
                        onChange={(e) => {
                          // Get the new value
                          const newValue = e.target.value;
                          // Allow only numbers, or if the input is empty (allow backspace/deletion)
                          if (
                            newValue === "" ||
                            (!isNaN(newValue) && Number(newValue) <= 200)
                          ) {
                            handleSymbolChange(
                              rtp,
                              `reel${selectedTab + 1}`,
                              symbol,
                              e.target.value
                            );
                          }
                        }}
                        inputProps={{
                          autoComplete: "off",
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                        }}
                      />
                    </Grid>
                  ))}
                  {midSymbols.map((symbol) => (
                    <Grid item xs={6} sm={4} key={symbol}>
                      <TextField
                        label={`Symbol ${symbol}`}
                        fullWidth
                        value={
                          symbolValues[rtp][`reel${selectedTab + 1}`][symbol]
                        }
                        onChange={(e) => {
                          // Get the new value
                          const newValue = e.target.value;
                          // Allow only numbers, or if the input is empty (allow backspace/deletion)
                          if (
                            newValue === "" ||
                            (!isNaN(newValue) && Number(newValue) <= 200)
                          ) {
                            handleSymbolChange(
                              rtp,
                              `reel${selectedTab + 1}`,
                              symbol,
                              e.target.value
                            );
                          }
                        }}
                        inputProps={{
                          autoComplete: "off",
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                        }}
                      />
                    </Grid>
                  ))}
                  {higherSymbols.map((symbol) => (
                    <Grid item xs={6} sm={4} key={symbol}>
                      <TextField
                        label={`Symbol ${symbol}`}
                        fullWidth
                        value={
                          symbolValues[rtp][`reel${selectedTab + 1}`][symbol]
                        }
                        onChange={(e) => {
                          // Get the new value
                          const newValue = e.target.value;
                          // Allow only numbers, or if the input is empty (allow backspace/deletion)
                          if (
                            newValue === "" ||
                            (!isNaN(newValue) && Number(newValue) <= 200)
                          ) {
                            handleSymbolChange(
                              rtp,
                              `reel${selectedTab + 1}`,
                              symbol,
                              e.target.value
                            );
                          }
                        }}
                        inputProps={{
                          autoComplete: "off",
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                        }}
                      />
                    </Grid>
                  ))}
                  {specialSymbols.map((symbol) => (
                    <Grid item xs={6} sm={4} key={symbol}>
                      <TextField
                        label={`Symbol ${symbol}`}
                        fullWidth
                        value={
                          symbolValues[rtp][`reel${selectedTab + 1}`][symbol]
                        }
                        onChange={(e) => {
                          // Get the new value
                          const newValue = e.target.value;
                          // Allow only numbers, or if the input is empty (allow backspace/deletion)
                          if (
                            newValue === "" ||
                            (!isNaN(newValue) && Number(newValue) <= 200)
                          ) {
                            handleSymbolChange(
                              rtp,
                              `reel${selectedTab + 1}`,
                              symbol,
                              e.target.value
                            );
                          }
                        }}
                        inputProps={{
                          autoComplete: "off",
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
            ))
          )}
        </Grid>

        {/* Submit Button - Only show for non-Firelink and non-Keno games */}
        {selectedGame && !isFirelinkGame(selectedGame?.name) && !isKenoGame(selectedGame?.name) && (
          <Grid item xs={12}>
            <Button variant="contained" color="primary" onClick={handleSubmit}>
              Submit
            </Button>
          </Grid>
        )}
      </Grid>
    </React.Fragment>
  );
};

export default NewCreateGameRTP;
